/**
 * Centralized z-index constants for consistent layering across the app.
 *
 * Usage:
 *   import { Z_INDEX } from '../utils/zIndex';
 *   style={{ zIndex: Z_INDEX.MODAL }}
 *
 * ⚠ There is a SECOND scale: the Tailwind `z-*` utilities in tailwind.config.js
 * (z-modal, z-overlay, z-dropdown, ...). The two disagree — Tailwind's `z-modal`
 * is 50, which is this file's MODAL_BACKDROP, one level BELOW MODAL (51) and two
 * below MODAL_CONTENT (52). So a `z-modal` overlay portaled to document.body
 * renders BEHIND any open <Modal>, which is exactly how the photo viewers ended
 * up behind the cards that launched them. When an overlay has to coexist with
 * <Modal> (which sets its z inline from these constants), use these constants —
 * not the class.
 *
 * Layer hierarchy (lowest to highest):
 * - Base content: 0-10
 * - Sticky headers/footers: 20-30
 * - Dropdowns/popovers: 40
 * - Modal backdrops: 50
 * - Modals: 51-59
 * - Tooltips: 60
 * - Feature hints/onboarding: 70-79
 * - Guided tours: 100-110
 * - Toast notifications: 120
 * - Critical system overlays: 200+
 */

export const Z_INDEX = {
  // Base layers
  BASE: 0,
  CONTENT: 1,

  // Elevated content
  STICKY_HEADER: 20,
  STICKY_FOOTER: 20,
  FLOATING_BUTTON: 25,

  // Interactive overlays
  DROPDOWN: 40,
  POPOVER: 40,
  AUTOCOMPLETE: 45,

  // Modal system
  MODAL_BACKDROP: 50,
  MODAL: 51,
  MODAL_CONTENT: 52,
  NESTED_MODAL_BACKDROP: 55,
  NESTED_MODAL: 56,
  NESTED_MODAL_CONTENT: 57,

  // Full-screen photo viewers (PhotoLightbox, PhotoPeekable's preview).
  //
  // This matters where a viewer is PORTALED to document.body while a modal is
  // open — PhotoPeekable (a photo on a review card inside the trip review form)
  // and MapInfoCard. There the viewer is a plain SIBLING of the modal, so
  // z-index alone decides, and anything at or below NESTED_MODAL_CONTENT paints
  // behind the card that launched it.
  //
  // It does NOT matter where a viewer is rendered INLINE inside a modal, as
  // PhotoStep does in the recap composer: Modal's content div is positioned with
  // z-index 52, so it is a stacking context and every value here is clamped
  // inside it. That is load-bearing, not a wart — it is why a nested
  // ConfirmDialog (57) still paints above an inline lightbox. Portaling one of
  // those to body at 58 would bury the confirm dialog under its backdrop.
  //
  // Above every modal token, below TOOLTIP.
  PHOTO_VIEWER: 58,

  // Tooltips
  TOOLTIP: 60,

  // Onboarding & Feature Discovery
  FEATURE_HINT: 70,
  FEATURE_HINT_BACKDROP: 71,
  COACH_MARK: 75,

  // Guided Tours (highest interactive layer)
  TOUR_BACKDROP: 100,
  TOUR_SPOTLIGHT: 101,
  TOUR_TOOLTIP: 105,
  TOUR_BEACON: 110,

  // Notifications
  TOAST: 120,

  // System level (rarely used)
  SYSTEM_OVERLAY: 200,
  CRITICAL: 9999
};

/**
 * CSS custom properties version for use in stylesheets
 * Add to your root CSS:
 *
 * :root {
 *   --z-modal: 51;
 *   --z-tooltip: 60;
 *   --z-tour: 100;
 *   --z-toast: 120;
 * }
 */
export const Z_INDEX_CSS_VARS = `
:root {
  --z-base: ${Z_INDEX.BASE};
  --z-sticky: ${Z_INDEX.STICKY_HEADER};
  --z-dropdown: ${Z_INDEX.DROPDOWN};
  --z-modal-backdrop: ${Z_INDEX.MODAL_BACKDROP};
  --z-modal: ${Z_INDEX.MODAL};
  --z-modal-content: ${Z_INDEX.MODAL_CONTENT};
  --z-tooltip: ${Z_INDEX.TOOLTIP};
  --z-feature-hint: ${Z_INDEX.FEATURE_HINT};
  --z-tour: ${Z_INDEX.TOUR_BACKDROP};
  --z-tour-tooltip: ${Z_INDEX.TOUR_TOOLTIP};
  --z-toast: ${Z_INDEX.TOAST};
}
`;

export default Z_INDEX;
